import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { FlaskConical, TrendingUp, TrendingDown, Minus, Calendar, DollarSign, Target } from 'lucide-react';
import { useProductBundles } from '@/hooks/useProductBundles';
import { useBundleConversionStats } from '@/hooks/useBundleConversion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, subMonths, isAfter, isBefore, parseISO } from 'date-fns';

export function BundleABTesting() {
  const { data: bundles } = useProductBundles();
  const { data: conversionStats } = useBundleConversionStats();
  const [bundleA, setBundleA] = useState<string>('');
  const [bundleB, setBundleB] = useState<string>('');
  const [timeRange, setTimeRange] = useState<'1m' | '3m' | '6m'>('3m');

  const getStatsForBundle = (bundleId: string) => {
    return conversionStats?.find(s => s.bundle_id === bundleId);
  };

  const statsA = bundleA ? getStatsForBundle(bundleA) : null;
  const statsB = bundleB ? getStatsForBundle(bundleB) : null;

  const bundleAData = bundles?.find(b => b.id === bundleA);
  const bundleBData = bundles?.find(b => b.id === bundleB);

  const calculateWinner = () => {
    if (!statsA || !statsB) return null;
    
    const scoreA = (statsA.conversion_rate * 0.4) + 
                   (statsA.total_won_revenue / 10000 * 0.4) + 
                   (statsA.total_deals * 0.2);
    const scoreB = (statsB.conversion_rate * 0.4) + 
                   (statsB.total_won_revenue / 10000 * 0.4) + 
                   (statsB.total_deals * 0.2);
    
    if (Math.abs(scoreA - scoreB) < 0.1) return 'tie';
    return scoreA > scoreB ? 'A' : 'B';
  };

  const winner = calculateWinner();

  const comparisonData = statsA && statsB ? [
    {
      metric: 'Conversion Rate',
      'Bundle A': statsA.conversion_rate,
      'Bundle B': statsB.conversion_rate,
    },
    {
      metric: 'Total Deals',
      'Bundle A': statsA.total_deals,
      'Bundle B': statsB.total_deals,
    },
    {
      metric: 'Won Deals',
      'Bundle A': statsA.won_deals,
      'Bundle B': statsB.won_deals,
    },
    {
      metric: 'Avg Revenue ($K)',
      'Bundle A': statsA.avg_won_deal_value / 1000,
      'Bundle B': statsB.avg_won_deal_value / 1000,
    },
  ] : [];

  const renderTrendIcon = (valueA: number, valueB: number, isA: boolean) => {
    const diff = valueA - valueB;
    if (Math.abs(diff) < 0.01) return <Minus className="h-4 w-4 text-muted-foreground" />;
    if (isA) {
      return diff > 0 ? <TrendingUp className="h-4 w-4 text-green-500" /> : <TrendingDown className="h-4 w-4 text-red-500" />;
    }
    return diff < 0 ? <TrendingUp className="h-4 w-4 text-green-500" /> : <TrendingDown className="h-4 w-4 text-red-500" />;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-primary" />
            <CardTitle>Bundle A/B Testing</CardTitle>
          </div>
          <Select value={timeRange} onValueChange={(v: '1m' | '3m' | '6m') => setTimeRange(v)}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1m">Last Month</SelectItem>
              <SelectItem value="3m">Last 3 Months</SelectItem>
              <SelectItem value="6m">Last 6 Months</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <CardDescription>Compare performance of two bundles to identify the best performer</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Bundle A</label>
            <Select value={bundleA} onValueChange={setBundleA}>
              <SelectTrigger>
                <SelectValue placeholder="Select first bundle" />
              </SelectTrigger>
              <SelectContent>
                {bundles?.filter(b => b.id !== bundleB).map(bundle => (
                  <SelectItem key={bundle.id} value={bundle.id}>{bundle.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Bundle B</label>
            <Select value={bundleB} onValueChange={setBundleB}>
              <SelectTrigger>
                <SelectValue placeholder="Select second bundle" />
              </SelectTrigger>
              <SelectContent>
                {bundles?.filter(b => b.id !== bundleA).map(bundle => (
                  <SelectItem key={bundle.id} value={bundle.id}>{bundle.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {bundleA && bundleB && statsA && statsB && (
          <>
            {/* Winner Banner */}
            <div className={`p-4 rounded-lg border-2 ${
              winner === 'A' ? 'border-green-500 bg-green-500/10' : 
              winner === 'B' ? 'border-blue-500 bg-blue-500/10' : 
              'border-muted bg-muted/50'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  <span className="font-semibold">
                    {winner === 'tie' ? 'Performance is similar' : 
                     winner === 'A' ? `${bundleAData?.name} is winning` : 
                     `${bundleBData?.name} is winning`}
                  </span>
                </div>
                {winner !== 'tie' && (
                  <Badge variant={winner === 'A' ? 'default' : 'secondary'}>
                    {winner === 'A' ? 
                      `+${((statsA.conversion_rate - statsB.conversion_rate)).toFixed(1)}% conversion` :
                      `+${((statsB.conversion_rate - statsA.conversion_rate)).toFixed(1)}% conversion`
                    }
                  </Badge>
                )}
              </div>
            </div>

            {/* Comparison Metrics */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Bundle A Stats */}
              <Card className={`${winner === 'A' ? 'ring-2 ring-green-500' : ''}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{bundleAData?.name}</CardTitle>
                    {winner === 'A' && <Badge className="bg-green-500">Winner</Badge>}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Conversion Rate</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{statsA.conversion_rate.toFixed(1)}%</span>
                      {renderTrendIcon(statsA.conversion_rate, statsB.conversion_rate, true)}
                    </div>
                  </div>
                  <Progress value={statsA.conversion_rate} className="h-2" />
                  
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div>
                      <p className="text-xs text-muted-foreground">Total Deals</p>
                      <p className="text-lg font-semibold">{statsA.total_deals}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Won Deals</p>
                      <p className="text-lg font-semibold text-green-500">{statsA.won_deals}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Revenue</p>
                      <p className="text-lg font-semibold">${statsA.total_won_revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Avg Deal Size</p>
                      <p className="text-lg font-semibold">${statsA.avg_won_deal_value.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bundle B Stats */}
              <Card className={`${winner === 'B' ? 'ring-2 ring-blue-500' : ''}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{bundleBData?.name}</CardTitle>
                    {winner === 'B' && <Badge className="bg-blue-500">Winner</Badge>}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Conversion Rate</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{statsB.conversion_rate.toFixed(1)}%</span>
                      {renderTrendIcon(statsB.conversion_rate, statsA.conversion_rate, true)}
                    </div>
                  </div>
                  <Progress value={statsB.conversion_rate} className="h-2" />
                  
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div>
                      <p className="text-xs text-muted-foreground">Total Deals</p>
                      <p className="text-lg font-semibold">{statsB.total_deals}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Won Deals</p>
                      <p className="text-lg font-semibold text-green-500">{statsB.won_deals}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Revenue</p>
                      <p className="text-lg font-semibold">${statsB.total_won_revenue.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Avg Deal Size</p>
                      <p className="text-lg font-semibold">${statsB.avg_won_deal_value.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Comparison Chart */}
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparisonData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis type="number" />
                  <YAxis dataKey="metric" type="category" width={120} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="Bundle A" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="Bundle B" fill="hsl(var(--secondary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {(!bundleA || !bundleB) && (
          <div className="text-center py-8 text-muted-foreground">
            <FlaskConical className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Select two bundles to compare their performance</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
